import { useEphemeralStore } from '@/lib/app-store';
import type { Locale } from '@worldcoo/monolith/core';
import { AnimateNumber } from '@worldcoo/ui/custom/animate-number';
import { useDebounce } from '@worldcoo/ui/hooks/use-debounce';
import { useEffect, useRef, useState } from 'react';

/** The 400ms hold starts only once every registered instance reports, so the bump waits for the slowest. */
let _holdTimer: ReturnType<typeof setTimeout> | undefined;
let _holdCallbacks: Array<() => void> = [];
let _expectedBumps = 0;

function tryFlushRelease() {
	if (_expectedBumps > 0 && _holdCallbacks.length >= _expectedBumps) {
		clearTimeout(_holdTimer);
		_holdTimer = setTimeout(() => {
			const cbs = _holdCallbacks;
			_holdCallbacks = [];
			_expectedBumps = 0;
			cbs.forEach((fn) => fn());
		}, 400);
	}
}

function registerBump() {
	_expectedBumps++;
}

function unregisterBump() {
	if (_expectedBumps > 0) _expectedBumps--;
	tryFlushRelease();
}

function scheduleBumpRelease(callback: () => void) {
	_holdCallbacks.push(callback);
	tryFlushRelease();
}

/**
 * Bumps the figure when a donation is confirmed, delayed ~700ms to peak as counting begins (debounce 1s).
 * Never cancelled when the shared `donationConfirmed` goes false: another instance may have cleared it.
 */
export function PortalAnimateNumber(props: {
	locale: Locale;
	value: number;
	suffix?: string;
	decimalDigits?: number;
	className?: string;
	testId?: string;
	instant?: boolean;
}) {
	const donationConfirmed = useEphemeralStore((store) => store.donationConfirmed);
	const setDonationConfirmed = useEphemeralStore((store) => store.setDonationConfirmed);
	const setDonationToConfirm = useEphemeralStore((store) => store.setDonationToConfirm);
	const debouncedValue = useDebounce(props.value, 1_000);
	const bump = donationConfirmed && props.suffix !== '%';
	const [bumpPhase, setBumpPhase] = useState<'idle' | 'up' | 'down'>('idle');
	const triggeredRef = useRef(false);
	const registeredRef = useRef(false);
	const completedRef = useRef(false);
	const spanRef = useRef<HTMLSpanElement>(null);
	const animRef = useRef<Animation>(undefined);

	// A registered instance that never reported would block the others' release.
	useEffect(() => {
		return () => {
			if (registeredRef.current && !completedRef.current) unregisterBump();
		};
	}, []);

	useEffect(() => {
		if (bump && bumpPhase === 'idle' && !triggeredRef.current) {
			// An unchanged counter never fires onAnimateComplete, so registering it would block the release forever.
			const valueWillChange = debouncedValue !== props.value;
			if (!valueWillChange) return;

			triggeredRef.current = true;
			const timer = setTimeout(() => {
				registerBump();
				registeredRef.current = true;
				completedRef.current = false;
				setBumpPhase('up');
			}, 700);
			return () => clearTimeout(timer);
		}
		if (!bump) {
			triggeredRef.current = false;
		}
	}, [bump, bumpPhase, debouncedValue, props.value]);

	useEffect(() => {
		const el = spanRef.current;
		if (!el) return;

		if (bumpPhase === 'up') {
			animRef.current?.cancel();
			el.style.display = 'inline-block';
			animRef.current = el.animate([{ transform: 'scale(1)' }, { transform: 'scale(1.5)' }], {
				duration: 300,
				fill: 'forwards',
				easing: 'ease',
			});
		} else if (bumpPhase === 'down') {
			// Committed first, so the down-animation starts from it.
			try {
				animRef.current?.commitStyles();
			} catch {
				/* already idle */
			}
			animRef.current?.cancel();
			animRef.current = el.animate([{ transform: 'scale(1.5)' }, { transform: 'scale(1)' }], {
				duration: 500,
				fill: 'forwards',
				easing: 'ease',
			});
			animRef.current.onfinish = () => {
				animRef.current?.cancel();
				el.style.transform = '';
				el.style.display = '';
				setBumpPhase('idle');
				setDonationConfirmed(false);
				setDonationToConfirm(undefined);
			};
		} else {
			if (animRef.current) {
				animRef.current.cancel();
				animRef.current = undefined;
				el.style.transform = '';
				el.style.display = '';
			}
		}
	}, [bumpPhase, setDonationConfirmed, setDonationToConfirm]);

	return (
		<span ref={spanRef}>
			<AnimateNumber
				locale={props.locale}
				value={donationConfirmed ? debouncedValue : props.value}
				suffix={props.suffix}
				decimalDigits={props.decimalDigits}
				testId={props.testId}
				// The count-up is the donation's feedback, and the bump is timed against it.
				instant={props.instant && !donationConfirmed}
				onAnimateComplete={() => {
					// One report per cycle: a second would release the bump while a sibling is still counting, and the
					// counter reports on every formatting or mode change.
					if (bumpPhase === 'up' && !completedRef.current) {
						completedRef.current = true;
						scheduleBumpRelease(() => setBumpPhase('down'));
					}
				}}
			/>
		</span>
	);
}
